/**
 * DynamicRolesPanel — Sprint 4 F5a (Admin UI'sı)
 *
 * Backend: /api/admin/roles, /api/admin/permissions, /api/admin/roles/{id}/permissions
 * Yetki: yonetim.rol_yonet (rol CRUD), yonetim.yetki_yonet (matrisi düzenleme)
 *
 * UI bölümleri:
 *  1) Rol listesi tablosu (sistem rolleri readonly badge)
 *  2) Yeni rol ekle dialogu
 *  3) Seçili rolün yetki matrisi:
 *     - Modül + alt-grup (subcategory) hiyerarşik gruplama
 *     - Modül başlığında "tümünü seç/kaldır" (indeterminate destekli)
 *     - Alt-grup başlığında "tümünü seç/kaldır"
 *     - Yetki arama / filtreleme
 *     - Preset şablonlar (Tümü / Sadece okuma / Hiçbiri)
 *     - Tooltip ile açıklama
 *
 * API stratejisi: bulk operasyonlar (modül toggle, preset) atomik tek PUT
 * isteğiyle çalışır; tek checkbox tıklaması mevcut POST/DELETE ile çalışır.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "@/components/ui/tabs";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "@/components/ui/tooltip";
import {
  Shield, Plus, Lock, ChevronRight, ChevronDown, RefreshCw,
  Search, Sparkles, Info, X, RotateCcw, PowerOff
} from "lucide-react";
import { toast } from "sonner";

import apiClient from "@/api/client";
import { useAuthorization } from "@/auth/AuthorizationContext";
import DeactivateRoleModal from "@/components/admin/DeactivateRoleModal";
import { normalizeSearchText } from "@/utils/stringUtils";
import { SearchableSelect } from "@/components/ui/SearchableSelect";


// ---------------------------------------------------------------------------
// Modül + alt-grup label haritaları (UI'da gösterim için)
// ---------------------------------------------------------------------------
const MODULE_LABELS = {
  audit:         "Audit",
  bakim:         "Bakım",
  beli:          "BELİ (AI)",
  bildirim:      "Bildirim",
  bolge:         "Bölge",
  faaliyet:      "Faaliyet",
  fotograf:      "Fotoğraf",
  izin:          "İzin",
  lokasyon:      "Lokasyon",
  malzeme:       "Malzeme",
  mesai:         "Mesai",
  onay:          "Onay",
  operasyon:     "Operasyon",
  organizasyon:  "Organizasyon",
  park:          "Park",
  pasif_alan:    "Pasif Alan",
  protokol_park: "Protokollü Park",
  personel:      "Personel",
  rapor:         "Rapor",
  revizyon:     "Revize",
  system:        "Sistem",
  takvim:        "Takvim",
  yonetim:       "Yönetim",
};

const SUBCATEGORY_LABELS = {
  filtre:   "Filtreler",
  aksiyon:  "Aksiyonlar",
  sosyal:   "Sosyal İzin",
  silme:    "Silme (Durum Bazlı)",
};

// Modül sıralama önceliği (alfabetik yerine kullanım sırası)
const MODULE_ORDER = [
  "park", "pasif_alan", "protokol_park", "bolge", "personel", "izin", "onay", "operasyon", "mesai", "malzeme",
  "revizyon", "bakim", "faaliyet", "rapor", "lokasyon", "takvim",
  "fotograf", "bildirim", "organizasyon", "audit", "yonetim", "system",
];

// ---------------------------------------------------------------------------
// Self-lockout & kritik yetki uyarıları (Sprint 4 F2)
// ---------------------------------------------------------------------------

// Actor kendi rolünden kaldırırsa panele erişimi kaybeder
const SELF_LOCKOUT_SLUGS = ["yonetim.rol_yonet", "yonetim.yetki_yonet"];

// Bu yetkileri kaldırmak hızlı bir confirm gerektirir (kullanıcı görünürlüğünü kırar)
const CRITICAL_VISIBILITY_SLUGS = [
  "park.gor", "park.listele",
  "izin.gor", "izin.listele",
  "personel.listele", "personel.gor",
  "bakim.gor", "mesai.gor",
];

// ---------------------------------------------------------------------------
// Preset şablonlar (rol için hızlı yetki seti uygulama)
// ---------------------------------------------------------------------------
const PRESETS = [
  {
    key: "all",
    label: "Tümü",
    description: "Tüm yetkileri ver (admin/başkan eşdeğeri)",
    matcher: () => true,
  },
  {
    key: "read_only",
    label: "Sadece Okuma",
    description: "Sadece görüntüleme yetkileri (.gor, .listele, .zincir_gor, .yorum_gor, .agac_gor, .arama)",
    matcher: (p) =>
      /\.(gor|listele|arama)$/.test(p.slug) ||
      /\.(zincir_gor|yorum_gor|agac_gor)$/.test(p.slug),
  },
  {
    key: "none",
    label: "Hiçbiri",
    description: "Tüm yetkileri kaldır (sıfırdan başla)",
    matcher: () => false,
  },
];


export default function DynamicRolesPanel({ onNavigateToOrganization } = {}) {
  const authz = useAuthorization();
  const { has, refresh: refreshAuthz, permissions: myPermissions, role: myRole, loading: authzLoading } = authz;
  const canManageRoles = has("yonetim.rol_yonet");
  const canManagePermissions = has("yonetim.yetki_yonet");

  // ---------- state ----------
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [rolePermsSet, setRolePermsSet] = useState(new Set());
  const [rolePermsLoading, setRolePermsLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Modül-bazlı UI state
  const [expandedModules, setExpandedModules] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    slug: "",
    display_name: "",
    hierarchy_level: "100",
    description: "",
  });
  const [cloneFromRoleId, setCloneFromRoleId] = useState("");
  const [creating, setCreating] = useState(false);

  // Aktif / Pasif sekme durumu
  const [activeTab, setActiveTab] = useState("active");

  // Pasifleştirme modal hedefi (role objesi). null → kapalı.
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // Etki analizi: seçili role kaç aktif personel atanmış
  const [roleImpact, setRoleImpact] = useState(null);

  // ---------- fetch ----------

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        apiClient.get("/admin/roles?include_system=true&include_deleted=true"),
        apiClient.get("/admin/permissions"),
      ]);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setPermissions(Array.isArray(permsRes.data) ? permsRes.data : []);
    } catch (err) {
      toast.error("Roller/yetkiler yüklenemedi: " + (err?.response?.data?.error?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoleImpact = useCallback(async (roleId) => {
    if (!roleId) {
      setRoleImpact(null);
      return;
    }
    try {
      const res = await apiClient.get(`/admin/roles/${roleId}/impact`);
      setRoleImpact(res.data?.affected_personnel_count ?? 0);
    } catch {
      // Endpoint yoksa veya hata olursa sessizce 0 kabul et
      setRoleImpact(null);
    }
  }, []);

  const fetchRolePerms = useCallback(async (roleId) => {
    if (!roleId) {
      setRolePermsSet(new Set());
      return;
    }
    setRolePermsLoading(true);
    try {
      const res = await apiClient.get(`/admin/roles/${roleId}/permissions`);
      const slugs = Array.isArray(res.data?.permissions) ? res.data.permissions : [];
      setRolePermsSet(new Set(slugs));
    } catch (err) {
      toast.error("Rol yetkileri alınamadı: " + (err?.response?.data?.error?.message || err.message));
    } finally {
      setRolePermsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageRoles || canManagePermissions) fetchAll();
  }, [canManageRoles, canManagePermissions, fetchAll]);

  useEffect(() => {
    if (selectedRoleId) {
      fetchRolePerms(selectedRoleId);
      fetchRoleImpact(selectedRoleId);
    } else {
      setRoleImpact(null);
    }
  }, [selectedRoleId, fetchRolePerms, fetchRoleImpact]);

  // ---------- actions ----------

  const handleCreateRole = async () => {
    if (!createForm.slug || !createForm.display_name) {
      toast.error("Slug ve görüntü adı zorunlu.");
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(createForm.slug)) {
      toast.error("Slug küçük harf + alt çizgi formatında olmalı (örn: 'bas_formen').");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        slug: createForm.slug.trim(),
        display_name: createForm.display_name.trim(),
        hierarchy_level: parseInt(createForm.hierarchy_level, 10) || 0,
        description: createForm.description.trim() || null,
        is_active: true,
      };
      const createRes = await apiClient.post("/admin/roles", payload);
      const newRoleId = createRes.data?.id;

      // Eğer "şu rolden kopyala" seçilmişse, yetkileri kopyala
      if (cloneFromRoleId && newRoleId) {
        try {
          await apiClient.post(`/admin/roles/${newRoleId}/clone-from/${cloneFromRoleId}`);
          const sourceRole = roles.find((r) => r.id === cloneFromRoleId);
          toast.success(
            `'${payload.display_name}' rolü eklendi ve '${sourceRole?.display_name || "kaynak"}' rolünden yetkiler kopyalandı.`
          );
        } catch (cloneErr) {
          toast.warning(
            `Rol eklendi ama yetki kopyalama başarısız: ${cloneErr?.response?.data?.error?.message || cloneErr.message}. Manuel olarak ekleyin.`
          );
        }
      } else {
        toast.success(`'${payload.display_name}' rolü eklendi (yetkisiz).`);
      }

      setIsCreateOpen(false);
      setCreateForm({ slug: "", display_name: "", hierarchy_level: "100", description: "" });
      setCloneFromRoleId("");
      await fetchAll();
    } catch (err) {
      toast.error("Rol eklenemedi: " + (err?.response?.data?.error?.message || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = (role) => {
    if (role.slug === "admin") {
      toast.error("admin rolü pasifleştirilemez.");
      return;
    }
    // Pasifleştirme akışı DeactivateRoleModal'a delege edilir — modal
    // /impact endpoint'inden etki analizi çeker, kullanıcıya gösterir,
    // onaylanırsa DELETE'i kendisi tetikler.
    setDeactivateTarget(role);
  };

  const handleRestoreRole = async (role) => {
    if (!window.confirm(
      `'${role.display_name}' rolünü tekrar aktif etmek istiyor musunuz?\n\n` +
      `Bu role atanmış personeller yeniden rolün yetkilerini kullanmaya başlayacak.`
    )) {
      return;
    }
    try {
      await apiClient.post(`/admin/roles/${role.id}/restore`);
      toast.success("Rol tekrar aktifleştirildi.");
      await fetchAll();
    } catch (err) {
      toast.error("Aktifleştirilemedi: " + (err?.response?.data?.error?.message || err.message));
    }
  };

  // Tek bir yetkiyi değiştir (checkbox tıklama)
  const handleTogglePermission = useCallback(async (permission) => {
    if (!selectedRoleId) return;
    if (!canManagePermissions) {
      toast.error("Yetki matrisini düzenleme yetkiniz yok.");
      return;
    }
    const currentlyGranted = rolePermsSet.has(permission.slug);
    // selectedRole'u inline hesapla — declaration sırasına bağımlı olmamak için
    const currentRole = roles.find((r) => r.id === selectedRoleId);

    // Self-lockout uyarısı: kendi rolünden kritik yönetim yetkisi kaldırma
    const isSelfRole = currentRole?.slug === myRole;
    const isRemovingCritical = currentlyGranted && SELF_LOCKOUT_SLUGS.includes(permission.slug);
    if (isSelfRole && isRemovingCritical) {
      if (!window.confirm(
        `'${permission.display_name}' yetkisini KENDİ rolünüzden kaldırıyorsunuz.\n\n` +
        `Bu işlemden sonra Rol/Yetki yönetim paneline erişiminizi kaybedeceksiniz. ` +
        `Tekrar erişmek için başka bir admin'in yardımı gerekir.\n\n` +
        `Yine de devam etmek istiyor musunuz?`
      )) {
        return;
      }
    }

    // Kritik görünürlük yetkisi + etkilenen kullanıcı varsa uyarı
    if (
      currentlyGranted
      && CRITICAL_VISIBILITY_SLUGS.includes(permission.slug)
      && (roleImpact ?? 0) > 0
    ) {
      if (!window.confirm(
        `'${permission.display_name}' yetkisini '${currentRole?.display_name}' rolünden kaldırıyorsunuz.\n\n` +
        `Bu role atanmış ${roleImpact} aktif personel etkilenecek — ` +
        `ilgili sayfa/işlevleri göremeyecekler.\n\n` +
        `Devam et?`
      )) {
        return;
      }
    }

    const previous = new Set(rolePermsSet);
    // Optimistik UI update
    const newSet = new Set(rolePermsSet);
    if (currentlyGranted) newSet.delete(permission.slug);
    else newSet.add(permission.slug);
    setRolePermsSet(newSet);

    try {
      if (currentlyGranted) {
        await apiClient.delete(`/admin/roles/${selectedRoleId}/permissions/${permission.id}`);
      } else {
        await apiClient.post(`/admin/roles/${selectedRoleId}/permissions/${permission.id}`);
      }
      // Yalnızca admin KENDİ rolünü değiştirdiyse kendi yetkilerini tazele.
      // Başka rol için refreshAuthz gereksiz; tüm ağaç re-render olup sayfa
      // en üste kayıyordu (kullanıcı kaldığı yerden devam edemiyordu).
      if (currentRole?.slug === myRole) refreshAuthz?.();
    } catch (err) {
      setRolePermsSet(previous);
      toast.error("Yetki değiştirilemedi: " + (err?.response?.data?.error?.message || err.message));
    }
  }, [canManagePermissions, refreshAuthz, rolePermsSet, selectedRoleId, roles, myRole, roleImpact]);

  // Bulk PUT — birden çok yetkiyi tek atomik istekle güncelle
  const applyBulkPermissions = useCallback(async (newSlugSet, label = "Toplu değişiklik") => {
    if (!selectedRoleId) return;
    if (!canManagePermissions) {
      toast.error("Yetki matrisini düzenleme yetkiniz yok.");
      return;
    }
    const role = roles.find((r) => r.id === selectedRoleId);

    // Self-lockout: kendi rolünde kritik yönetim yetkisi kayboluyorsa uyar
    if (role?.slug === myRole) {
      const removingCritical = SELF_LOCKOUT_SLUGS.filter(
        (s) => rolePermsSet.has(s) && !newSlugSet.has(s)
      );
      if (removingCritical.length > 0) {
        if (!window.confirm(
          `Bu toplu işlem KENDİ rolünüzden şu yetkileri kaldırıyor:\n` +
          `  • ${removingCritical.join("\n  • ")}\n\n` +
          `Bu işlemden sonra Rol/Yetki yönetim paneline erişiminizi kaybedeceksiniz. ` +
          `Tekrar erişmek için başka bir admin'in yardımı gerekir.\n\n` +
          `Yine de devam et?`
        )) {
          return;
        }
      }
    }

    // Kritik görünürlük yetkilerinin toplu kaybı + impact varsa uyar
    if ((roleImpact ?? 0) > 0) {
      const losingVisibility = CRITICAL_VISIBILITY_SLUGS.filter(
        (s) => rolePermsSet.has(s) && !newSlugSet.has(s)
      );
      if (losingVisibility.length > 0) {
        if (!window.confirm(
          `Bu işlem '${role?.display_name}' rolünden şu kritik görünürlük yetkilerini kaldıracak:\n` +
          `  • ${losingVisibility.join("\n  • ")}\n\n` +
          `${roleImpact} aktif personel etkilenecek — ilgili sayfa/işlevleri göremeyecekler.\n\n` +
          `Devam et?`
        )) {
          return;
        }
      }
    }

    // Admin için system.tum_yetki kalmak zorunda — UI guard (backend de doğrular)
    if (role?.slug === "admin" && !newSlugSet.has("system.tum_yetki")) {
      newSlugSet.add("system.tum_yetki");
    }
    // slug → id eşle
    const slugToId = new Map(permissions.map((p) => [p.slug, p.id]));
    const permission_ids = Array.from(newSlugSet)
      .map((slug) => slugToId.get(slug))
      .filter(Boolean);

    const previous = new Set(rolePermsSet);
    setRolePermsSet(newSlugSet);
    setBulkBusy(true);
    try {
      await apiClient.put(`/admin/roles/${selectedRoleId}/permissions`, {
        role_id: selectedRoleId,
        permission_ids,
      });
      const diff = newSlugSet.size - previous.size;
      const diffLabel = diff > 0 ? `+${diff} eklendi` : diff < 0 ? `${diff} kaldırıldı` : "değişiklik yok";
      toast.success(`${label}: ${diffLabel}`);
      if (role?.slug === myRole) refreshAuthz?.();
    } catch (err) {
      setRolePermsSet(previous);
      toast.error("Toplu güncelleme başarısız: " + (err?.response?.data?.error?.message || err.message));
    } finally {
      setBulkBusy(false);
    }
  }, [canManagePermissions, permissions, refreshAuthz, roles, rolePermsSet, selectedRoleId, myRole, roleImpact]);

  const handleSelectAllInGroup = useCallback((groupPerms, shouldSelect) => {
    const newSet = new Set(rolePermsSet);
    for (const p of groupPerms) {
      if (shouldSelect) newSet.add(p.slug);
      else newSet.delete(p.slug);
    }
    applyBulkPermissions(newSet, shouldSelect ? "Grup tümü seçildi" : "Grup tümü kaldırıldı");
  }, [applyBulkPermissions, rolePermsSet]);

  const handleApplyPreset = useCallback((preset) => {
    const newSet = new Set();
    for (const p of permissions) {
      if (preset.matcher(p)) newSet.add(p.slug);
    }
    applyBulkPermissions(newSet, `Şablon: ${preset.label}`);
  }, [applyBulkPermissions, permissions]);

  // ---------- derived ----------

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedRoleId) || null,
    [roles, selectedRoleId]
  );

  // soft_delete_role() hem deleted_at hem is_active=false set ediyor;
  // backend RoleOut'ta deleted_at alanı yok → is_active tek doğruluk kaynağı.
  const activeRoles = useMemo(
    () => roles.filter((r) => r.is_active),
    [roles]
  );
  const inactiveRoles = useMemo(
    () => roles.filter((r) => !r.is_active),
    [roles]
  );

  const isSelectedRoleInactive = selectedRole != null && !selectedRole.is_active;

  // Permission filtreleme (arama)
  const filteredPermissions = useMemo(() => {
    const q = normalizeSearchText(searchTerm);
    if (!q) return permissions;
    return permissions.filter((p) =>
      normalizeSearchText(p.slug).includes(q) ||
      normalizeSearchText(p.display_name).includes(q) ||
      normalizeSearchText(p.description).includes(q) ||
      normalizeSearchText(MODULE_LABELS[p.module] || p.module).includes(q)
    );
  }, [permissions, searchTerm]);

  // Permission'ları modül → subcategory → liste şeklinde grupla
  const groupedPerms = useMemo(() => {
    const moduleMap = new Map();
    for (const p of filteredPermissions) {
      if (!moduleMap.has(p.module)) moduleMap.set(p.module, new Map());
      const subKey = p.subcategory || "_default";
      const subMap = moduleMap.get(p.module);
      if (!subMap.has(subKey)) subMap.set(subKey, []);
      subMap.get(subKey).push(p);
    }
    // Sıralama
    const sortedModules = Array.from(moduleMap.entries()).sort(([a], [b]) => {
      const ia = MODULE_ORDER.indexOf(a);
      const ib = MODULE_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return sortedModules.map(([module, subMap]) => {
      const subs = Array.from(subMap.entries()).sort(([a], [b]) => {
        // "_default" en başta gelsin, diğerleri alfabetik
        if (a === "_default") return -1;
        if (b === "_default") return 1;
        return a.localeCompare(b);
      });
      // Her alt-grup içinde yetkileri slug'a göre sırala
      const subsSorted = subs.map(([sub, perms]) => [
        sub,
        perms.slice().sort((x, y) => x.slug.localeCompare(y.slug)),
      ]);
      const allPerms = subsSorted.flatMap(([, perms]) => perms);
      return { module, subs: subsSorted, allPerms };
    });
  }, [filteredPermissions]);

  // Bir grup için 'all' | 'none' | 'some' state'i hesapla
  const getGroupState = useCallback((groupPerms) => {
    if (!groupPerms.length) return "none";
    const selected = groupPerms.filter((p) => rolePermsSet.has(p.slug)).length;
    if (selected === 0) return "none";
    if (selected === groupPerms.length) return "all";
    return "some";
  }, [rolePermsSet]);

  const toggleModuleExpand = (module) => {
    const next = new Set(expandedModules);
    if (next.has(module)) next.delete(module);
    else next.add(module);
    setExpandedModules(next);
  };

  // Arama yapıldığında otomatik olarak eşleşen modülleri aç
  useEffect(() => {
    if (searchTerm.trim()) {
      setExpandedModules(new Set(groupedPerms.map((g) => g.module)));
    }
  }, [searchTerm, groupedPerms]);

  // ---------- render ----------

  if (authzLoading) {
    return (
      <Card className="border-gray-200">
        <CardContent className="py-6 text-center text-gray-500">
          ⏳ Yetkiler yükleniyor...
        </CardContent>
      </Card>
    );
  }

  if (!canManageRoles && !canManagePermissions) {
    return (
      <Card className="border-yellow-300 bg-yellow-50">
        <CardContent className="py-6">
          <div className="text-center">
            <p className="text-yellow-800 font-semibold mb-2">
              ⚠️ Bu paneli görüntülemek için yetkiniz yok
            </p>
            <p className="text-sm text-yellow-700">
              Gerekli yetkiler: <code className="bg-yellow-100 px-2 py-0.5 rounded">yonetim.rol_yonet</code> veya{" "}
              <code className="bg-yellow-100 px-2 py-0.5 rounded">yonetim.yetki_yonet</code>
            </p>
            <p className="text-xs text-yellow-600 mt-2">
              Rol: <strong>{myRole || "(bilinmiyor)"}</strong> · Yetki sayısı: <strong>{myPermissions?.length || 0}</strong>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Card className="border-purple-200">
        <CardHeader className="bg-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-purple-700" />
              <div>
                <CardTitle className="text-purple-700">Dinamik Rol & Yetki Yönetimi</CardTitle>
                <CardDescription>
                  Roller dinamik yönetilir. Sistem rolleri silinemez. Yetki değiştirme anında etkilidir.
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={fetchAll}
                disabled={loading}
                className="text-purple-600 border-purple-300"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
                Yenile
              </Button>
              {canManageRoles && (
                <Button
                  size="sm"
                  onClick={() => setIsCreateOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Yeni Rol
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Yükleniyor...</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SOL: Rol listesi (Aktif / Pasif sekmeleri) */}
              <div>
                <Tabs value={activeTab} onValueChange={(v) => {
                  setActiveTab(v);
                  setSelectedRoleId(null);
                }}>
                  <TabsList className="grid w-full grid-cols-2 mb-3 bg-purple-50">
                    <TabsTrigger
                      value="active"
                      className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm"
                    >
                      Aktif Roller ({activeRoles.length})
                    </TabsTrigger>
                    <TabsTrigger
                      value="inactive"
                      className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm"
                    >
                      Pasif Roller ({inactiveRoles.length})
                    </TabsTrigger>
                  </TabsList>

                  {/* AKTİF roller */}
                  <TabsContent value="active" className="mt-0">
                    <div className="border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Rol</TableHead>
                            <TableHead className="text-center">Seviye</TableHead>
                            <TableHead className="text-center">Tip</TableHead>
                            <TableHead className="text-right">İşlem</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeRoles.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-sm text-gray-500 py-6">
                                Henüz aktif rol yok.
                              </TableCell>
                            </TableRow>
                          )}
                          {activeRoles.map((role) => (
                            <TableRow
                              key={role.id}
                              className={`cursor-pointer ${selectedRoleId === role.id ? "bg-purple-50" : ""}`}
                              onClick={() => setSelectedRoleId(role.id)}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <ChevronRight
                                    className={`h-3 w-3 transition-transform ${selectedRoleId === role.id ? "rotate-90" : ""}`}
                                  />
                                  <div>
                                    <div className="font-medium">{role.display_name}</div>
                                    <div className="text-xs text-gray-500 font-mono">{role.slug}</div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-mono text-sm">
                                {Math.round(Number(role.hierarchy_level))}
                              </TableCell>
                              <TableCell className="text-center">
                                {role.is_system ? (
                                  <Badge variant="secondary" className="text-xs">
                                    <Lock className="h-2 w-2 mr-1" />
                                    Sistem
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">Özel</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {role.slug !== "admin" && canManageRoles && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-600 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteRole(role);
                                        }}
                                      >
                                        <PowerOff className="h-3 w-3 mr-1" />
                                        Pasifleştir
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs max-w-xs">
                                        Rolü devre dışı bırakır. "Pasif Roller" sekmesinden geri alabilirsiniz.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {role.slug === "admin" && (
                                  <span className="text-xs text-gray-400 italic">Korumalı</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* PASİF roller */}
                  <TabsContent value="inactive" className="mt-0">
                    <div className="border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Rol</TableHead>
                            <TableHead className="text-center">Seviye</TableHead>
                            <TableHead className="text-center">Tip</TableHead>
                            <TableHead className="text-right">İşlem</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inactiveRoles.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-sm text-gray-500 py-6">
                                Pasif rol bulunmuyor.
                              </TableCell>
                            </TableRow>
                          )}
                          {inactiveRoles.map((role) => (
                            <TableRow
                              key={role.id}
                              className={`cursor-pointer opacity-75 hover:opacity-100 ${selectedRoleId === role.id ? "bg-purple-50 opacity-100" : ""}`}
                              onClick={() => setSelectedRoleId(role.id)}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <ChevronRight
                                    className={`h-3 w-3 transition-transform ${selectedRoleId === role.id ? "rotate-90" : ""}`}
                                  />
                                  <div>
                                    <div className="font-medium text-gray-600">{role.display_name}</div>
                                    <div className="text-xs text-gray-400 font-mono">{role.slug}</div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-mono text-sm text-gray-500">
                                {Math.round(Number(role.hierarchy_level))}
                              </TableCell>
                              <TableCell className="text-center">
                                {role.is_system ? (
                                  <Badge variant="secondary" className="text-xs">
                                    <Lock className="h-2 w-2 mr-1" />
                                    Sistem
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">Özel</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {canManageRoles && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-green-700 hover:bg-green-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRestoreRole(role);
                                        }}
                                      >
                                        <RotateCcw className="h-3 w-3 mr-1" />
                                        Aktifleştir
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs max-w-xs">
                                        Rolü tekrar aktif eder. Personeller yetkilerini geri kazanır.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* SAĞ: Yetki matrisi (seçili rol için) */}
              <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center gap-2 flex-wrap">
                    {selectedRole
                      ? `'${selectedRole.display_name}' yetkileri (${rolePermsSet.size}/${permissions.length})`
                      : "Yetki Matrisi"}
                    {selectedRole && roleImpact !== null && (
                      <Badge
                        variant={roleImpact > 0 ? "destructive" : "secondary"}
                        className="text-xs"
                        title="Bu role atanmış aktif personel sayısı"
                      >
                        👥 {roleImpact} aktif personel
                      </Badge>
                    )}
                    {selectedRole && selectedRole.slug === myRole && (
                      <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">
                        ⚠️ Kendi rolünüz
                      </Badge>
                    )}
                    {isSelectedRoleInactive && (
                      <Badge variant="outline" className="text-xs border-gray-400 text-gray-600">
                        💤 Pasif rol
                      </Badge>
                    )}
                  </h3>
                  {selectedRole && canManagePermissions && !isSelectedRoleInactive && (
                    <div className="flex items-center gap-1">
                      {PRESETS.map((preset) => (
                        <Tooltip key={preset.key}>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 px-2 border-purple-200"
                              onClick={() => handleApplyPreset(preset)}
                              disabled={bulkBusy || rolePermsLoading}
                            >
                              <Sparkles className="h-3 w-3 mr-1" />
                              {preset.label}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs max-w-xs">{preset.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>

                {isSelectedRoleInactive && (
                  <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      Bu rol <strong>pasif</strong>. Yetkileri görüntülenebilir ancak değiştirilemez.
                      Düzenlemek için önce "Pasif Roller" sekmesinden <strong>Aktifleştir</strong>.
                    </span>
                  </div>
                )}

                {!selectedRole && (
                  <div className="border rounded p-6 text-center text-gray-500 text-sm">
                    Yetkilerini görüntülemek için soldaki listeden bir rol seçin.
                  </div>
                )}

                {selectedRole && (
                  <>
                    {/* Arama kutusu */}
                    <div className="relative mb-2">
                      <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                      <Input
                        placeholder="Yetki ara (slug, isim, açıklama veya modül)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-8 h-8 text-sm"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                          aria-label="Aramayı temizle"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    {rolePermsLoading && (
                      <p className="text-center text-gray-500 py-4">Yükleniyor...</p>
                    )}

                    {!rolePermsLoading && (
                      <div className="border rounded max-h-[500px] overflow-y-auto">
                        {groupedPerms.length === 0 && (
                          <div className="p-4 text-sm text-center text-gray-500">
                            Aramayla eşleşen yetki yok.
                          </div>
                        )}
                        {groupedPerms.map(({ module, subs, allPerms }) => {
                          const moduleState = getGroupState(allPerms);
                          const isExpanded = expandedModules.has(module) || !!searchTerm.trim();
                          const moduleLabel = MODULE_LABELS[module] || module;
                          return (
                            <div key={module} className="border-b last:border-b-0">
                              {/* Modül başlığı */}
                              <div className="bg-gray-50 px-3 py-2 flex items-center gap-2 sticky top-0 z-10">
                                <button
                                  type="button"
                                  onClick={() => toggleModuleExpand(module)}
                                  className="text-gray-600 hover:text-gray-900"
                                  aria-label={isExpanded ? "Daralt" : "Genişlet"}
                                >
                                  {isExpanded
                                    ? <ChevronDown className="h-3.5 w-3.5" />
                                    : <ChevronRight className="h-3.5 w-3.5" />}
                                </button>
                                <Checkbox
                                  checked={moduleState === "all"}
                                  data-state={moduleState === "some" ? "indeterminate" : undefined}
                                  disabled={!canManagePermissions || bulkBusy || isSelectedRoleInactive}
                                  onCheckedChange={(checked) =>
                                    handleSelectAllInGroup(allPerms, checked === true || moduleState !== "all")
                                  }
                                  className="data-[state=indeterminate]:bg-purple-300"
                                />
                                <span className="text-xs font-bold uppercase tracking-wide text-gray-700 flex-1">
                                  {moduleLabel}
                                </span>
                                <span className="text-[10px] text-gray-500 font-mono">
                                  {allPerms.filter((p) => rolePermsSet.has(p.slug)).length}/{allPerms.length}
                                </span>
                              </div>

                              {/* Modül içeriği */}
                              {isExpanded && (
                                <div>
                                  {subs.map(([subKey, perms]) => {
                                    const subState = getGroupState(perms);
                                    const subLabel = subKey === "_default"
                                      ? null
                                      : (SUBCATEGORY_LABELS[subKey] || subKey);
                                    return (
                                      <div key={subKey}>
                                        {subLabel && (
                                          <div className="bg-gray-25 border-t border-b px-3 py-1.5 flex items-center gap-2">
                                            <span className="w-3.5"></span>
                                            <Checkbox
                                              checked={subState === "all"}
                                              data-state={subState === "some" ? "indeterminate" : undefined}
                                              disabled={!canManagePermissions || bulkBusy || isSelectedRoleInactive}
                                              onCheckedChange={(checked) =>
                                                handleSelectAllInGroup(perms, checked === true || subState !== "all")
                                              }
                                              className="data-[state=indeterminate]:bg-purple-300"
                                            />
                                            <span className="text-xs font-semibold text-gray-600 flex-1">
                                              {subLabel}
                                            </span>
                                            <span className="text-[10px] text-gray-500 font-mono">
                                              {perms.filter((p) => rolePermsSet.has(p.slug)).length}/{perms.length}
                                            </span>
                                          </div>
                                        )}
                                        <div className="divide-y">
                                          {perms.map((p) => {
                                            const checked = rolePermsSet.has(p.slug);
                                            const isAdminTumYetki =
                                              selectedRole.slug === "admin" && p.slug === "system.tum_yetki";
                                            return (
                                              <label
                                                key={p.id}
                                                className={`flex items-start gap-3 pl-8 pr-3 py-2 cursor-pointer hover:bg-gray-50 ${
                                                  isAdminTumYetki ? "opacity-60 cursor-not-allowed" : ""
                                                }`}
                                              >
                                                <Checkbox
                                                  checked={checked}
                                                  disabled={!canManagePermissions || isAdminTumYetki || bulkBusy || isSelectedRoleInactive}
                                                  onCheckedChange={() => !isAdminTumYetki && !isSelectedRoleInactive && handleTogglePermission(p)}
                                                  className="mt-0.5"
                                                />
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-sm font-medium">{p.display_name}</span>
                                                    {p.description && (
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <Info className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          <p className="text-xs max-w-xs">{p.description}</p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    )}
                                                  </div>
                                                  <div className="text-xs text-gray-500 font-mono">{p.slug}</div>
                                                  {isAdminTumYetki && (
                                                    <div className="text-xs text-amber-600 mt-1">
                                                      🔒 admin rolünden bu yetki kaldırılamaz
                                                    </div>
                                                  )}
                                                </div>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>

        {/* Yeni Rol Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Rol Oluştur</DialogTitle>
              <DialogDescription>
                Slug: küçük harf + alt çizgi (örn. <code>bas_formen</code>).
                Seviye: tam sayı (örn. 250 = Formen ile Bölge Müh. arası).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="role-slug">Slug *</Label>
                <Input
                  id="role-slug"
                  value={createForm.slug}
                  onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value })}
                  placeholder="bas_formen"
                />
              </div>
              <div>
                <Label htmlFor="role-name">Görüntü Adı *</Label>
                <Input
                  id="role-name"
                  value={createForm.display_name}
                  onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
                  placeholder="Başformen"
                />
              </div>
              <div>
                <Label htmlFor="role-level">Seviye *</Label>
                <Input
                  id="role-level"
                  type="number"
                  step="1"
                  min="0"
                  value={createForm.hierarchy_level}
                  onChange={(e) => setCreateForm({ ...createForm, hierarchy_level: e.target.value })}
                  placeholder="250"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mevcut seviyeler: Park Görevlisi/Çim Ekibi=100, Formen=200,
                  Bölge Müh.=300, Müdür Yrd.=400, Müdür=500, Başkan=600, Admin=900.
                  Ara katmanlar için 150, 250 gibi değerler kullanılabilir.
                </p>
              </div>
              <div>
                <Label htmlFor="role-desc">Açıklama (opsiyonel)</Label>
                <Textarea
                  id="role-desc"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Bu rolün ne işe yaradığı..."
                  rows={2}
                />
              </div>
              <div className="pt-2 border-t">
                <Label htmlFor="role-clone-from">
                  Yetkileri başka bir rolden kopyala (opsiyonel)
                </Label>
                <SearchableSelect
                  value={cloneFromRoleId || "__none__"}
                  onChange={(v) => setCloneFromRoleId(v === "__none__" ? "" : v)}
                  allLabel="— Boş başla (yetkisiz) —"
                  allValue="__none__"
                  searchPlaceholder="Rol ara..."
                  options={roles
                    .filter((r) => r.is_active)
                    .map((r) => ({
                      id: r.id,
                      name: `${r.display_name} (${r.slug})`,
                    }))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Seçilirse yeni rol o rolün tüm yetkilerini başlangıçta alır. Sonra düzenleyebilirsiniz.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateOpen(false);
                  setCloneFromRoleId("");
                }}
                disabled={creating}
              >
                İptal
              </Button>
              <Button
                onClick={handleCreateRole}
                disabled={creating}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {creating ? "Oluşturuluyor..." : "Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pasifleştirme uyarı modal'ı — etki analizi + onay */}
        {deactivateTarget && (
          <DeactivateRoleModal
            role={deactivateTarget}
            onClose={() => setDeactivateTarget(null)}
            onComplete={async () => {
              if (selectedRoleId === deactivateTarget.id) setSelectedRoleId(null);
              setDeactivateTarget(null);
              await fetchAll();
            }}
            onNavigateToOrganization={onNavigateToOrganization}
          />
        )}
      </Card>
    </TooltipProvider>
  );
}
